/**
 * MD-11 SourceCard — Rich citation card (LexisNexis Protege-inspired).
 *
 * Spec: docs/superpowers/specs/2026-05-11-chat-leftbar-source-upgrade-design.md §3.4
 * DECISIONS: s2026-05-11-CHAT-LEFTBAR-SOURCE-UPGRADE
 *
 * Replaces inline `<div className="asklaw-chat__citation-card">` trong
 * chat.tsx CitationAccordion (legacy collapsed source list).
 *
 * Composition:
 *   [doc-type-icon] DocName              [shield] [confidence]
 *   So 45/2019/QH14 | Quoc hoi
 *   Provision path Dieu X Khoan Y
 *   Snippet "..."
 *   [eff-status badge] [Mo vbpl.vn ↗] [v N nguon khac] [Luu bookmark]
 *
 * a11y: focus-visible amber outline, aria-label per button, tooltip via title.
 */

import { useState, useEffect, useCallback, useMemo, useRef, type ReactElement } from 'react';
import type { CitationShape } from '../../../src/workers/refusal/types';
import {
  parseDocMeta,
  deriveSnippet,
  displayDocName,
  docTypeLabel,
  type DocType,
} from '../lib/citation-meta';
import { EffStatusBadge } from './corpus/EffStatusBadge';
import {
  addToLibrary,
  isInLibrary,
  removeFromLibrary,
  libraryItemId,
} from '../lib/library-store';
import { SOURCES, buildVbplUrl } from './CitationLink';

// ---- Icons ----

const baseIconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function DocTypeIcon({ type }: { type: DocType }): ReactElement {
  // 20x20 stroke 1.75 (lucide-style)
  const props = { ...baseIconProps, width: 20, height: 20, 'aria-hidden': true } as const;
  switch (type) {
    case 'LUAT':
    case 'BO_LUAT':
      // scale icon
      return (
        <svg {...props}>
          <path d="M12 3v18M3 7h18M5 7l2 7h4M19 7l-2 7h-4M7 14a2 2 0 0 1-4 0M21 14a2 2 0 0 1-4 0" />
        </svg>
      );
    case 'ND':
    case 'TT':
      // file-text
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6M9 13h6M9 17h6M9 9h2" />
        </svg>
      );
    case 'NQ':
      // file
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
      );
    case 'AL':
      // gavel
      return (
        <svg {...props}>
          <path d="M14 14L4 24M11 11l3-3M16 6l3-3M6 16l3-3M7 12l5 5M12 7l5 5" />
        </svg>
      );
    case 'HP':
      // landmark
      return (
        <svg {...props}>
          <path d="M3 22h18M5 22V11M19 22V11M9 22v-7M15 22v-7M3 11l9-7 9 7" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5V4.5A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
  }
}

function IconShield({ tone }: { tone: 'success' | 'warning' }): ReactElement {
  return (
    <svg
      {...baseIconProps}
      width={14}
      height={14}
      aria-hidden="true"
      style={{ color: tone === 'success' ? '#10b981' : '#F5B800' }}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      {tone === 'success' ? <path d="M9 12l2 2 4-4" /> : <path d="M12 8v5M12 16h0" />}
    </svg>
  );
}

function IconTriangle(): ReactElement {
  return (
    <svg
      {...baseIconProps}
      width={12}
      height={12}
      aria-hidden="true"
      style={{ color: '#c0392b' }}
    >
      <path d="M12 2L1 22h22z" />
      <path d="M12 9v5M12 17h0" />
    </svg>
  );
}

function IconBookmark({ filled }: { filled?: boolean }): ReactElement {
  return (
    <svg
      {...baseIconProps}
      width={14}
      height={14}
      fill={filled ? 'currentColor' : 'none'}
      aria-hidden="true"
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconExternal(): ReactElement {
  return (
    <svg {...baseIconProps} width={12} height={12} aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6M10 14L21 3" />
    </svg>
  );
}

function IconChevron(): ReactElement {
  return (
    <svg {...baseIconProps} width={12} height={12} aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// ---- Props ----

export type SourceCardProps = {
  citation: CitationShape;
  /** Full assistant message content — to derive snippet via span_start/span_end. */
  assistantContent?: string;
  lang: 'vi' | 'en';
  isPulsing?: boolean;
  onLibraryChange?: () => void;
};

// ---- Component ----

export function SourceCard({
  citation,
  assistantContent,
  lang,
  isPulsing,
  onLibraryChange,
}: SourceCardProps): ReactElement {
  const ref = (citation.ref ?? {}) as Partial<{
    doc_id: string;
    article_id: string;
    chunk_id: string;
    char_start: number;
    char_end: number;
    confidence: number;
    eff_status_at_render: 'CHL' | 'CCHL' | 'HHL' | 'TNHL' | 'KCPH' | 'KXĐ';
    provision_path: string;
    source_tier: 'vbpl_primary' | 'web_unverified';
    snippet: string;
    source_url: string;
    doc_name: string;
    doc_number: string;
  }>;

  const docMeta = useMemo(() => {
    const m = parseDocMeta(ref.doc_id ?? '', lang);
    // Server may provide richer doc_name / doc_number — prefer them.
    if (ref.doc_name) m.doc_name = ref.doc_name;
    if (ref.doc_number) m.doc_number = ref.doc_number;
    return m;
  }, [ref.doc_id, ref.doc_name, ref.doc_number, lang]);

  const snippet = useMemo(() => {
    if (ref.snippet) return ref.snippet;
    if (
      assistantContent &&
      typeof citation.span_start === 'number' &&
      typeof citation.span_end === 'number'
    ) {
      return deriveSnippet(assistantContent, citation.span_start, citation.span_end, 80);
    }
    return '';
  }, [ref.snippet, assistantContent, citation.span_start, citation.span_end]);

  const sourceTier = ref.source_tier ?? 'vbpl_primary';
  const isPrimary = sourceTier === 'vbpl_primary';
  const confidence = typeof ref.confidence === 'number' ? ref.confidence : null;
  const confLow = confidence !== null && confidence < 0.7;
  const confPct = confidence !== null ? Math.round(confidence * 100) : null;

  const docName = displayDocName(docMeta, lang);
  const docTypeName = docTypeLabel(docMeta.doc_type, lang);

  // Library state
  const provisionPath = ref.provision_path ?? '';
  const docId = ref.doc_id ?? '';
  const libId = libraryItemId(docId, provisionPath);
  const [saved, setSaved] = useState<boolean>(() => isInLibrary(docId, provisionPath));

  useEffect(() => {
    setSaved(isInLibrary(docId, provisionPath));
  }, [docId, provisionPath]);

  const handleToggleLibrary = useCallback(() => {
    if (!docId || !provisionPath) return;
    if (saved) {
      removeFromLibrary(libId);
      setSaved(false);
    } else {
      addToLibrary({
        doc_id: docId,
        doc_number: docMeta.doc_number,
        doc_name: docName,
        provision_path: provisionPath,
        eff_status: ref.eff_status_at_render,
        source_url: ref.source_url,
        source_tier: sourceTier,
      });
      setSaved(true);
    }
    onLibraryChange?.();
  }, [
    docId,
    provisionPath,
    saved,
    libId,
    docMeta.doc_number,
    docName,
    ref.eff_status_at_render,
    ref.source_url,
    sourceTier,
    onLibraryChange,
  ]);

  // Multi-source dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!dropdownOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        e.target instanceof Node &&
        !dropdownRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [dropdownOpen]);

  const primaryUrl =
    ref.source_url ||
    (docMeta.doc_number ? buildVbplUrl(docMeta.doc_number) : buildVbplUrl(docName));

  // Other sources for dropdown — skip vbpl (primary link), use rest
  const otherSources = SOURCES.filter((s) => s.id !== 'vbpl');

  return (
    <article
      data-cite-card-cid={citation.cid}
      data-pulse={isPulsing ? 'true' : 'false'}
      className={
        isPulsing
          ? 'asklaw-source-card asklaw-source-card--pulse'
          : 'asklaw-source-card'
      }
      aria-label={`${docTypeName}: ${docName} ${provisionPath ?? ''}`.trim()}
    >
      <header className="asklaw-source-card__header">
        <span className="asklaw-source-card__type-icon" aria-hidden="true">
          <DocTypeIcon type={docMeta.doc_type} />
        </span>
        <h3 className="asklaw-source-card__title">{docName}</h3>
        <div className="asklaw-source-card__badges">
          <span
            className="asklaw-source-card__shield"
            title={
              isPrimary
                ? lang === 'vi'
                  ? 'Nguồn vbpl.moj.gov.vn xác thực'
                  : 'Verified source vbpl.moj.gov.vn'
                : lang === 'vi'
                  ? 'Nguồn web — chưa xác thực'
                  : 'Web source — unverified'
            }
            aria-label={
              isPrimary
                ? lang === 'vi'
                  ? 'Nguồn xác thực'
                  : 'Verified source'
                : lang === 'vi'
                  ? 'Nguồn chưa xác thực'
                  : 'Unverified source'
            }
          >
            <IconShield tone={isPrimary ? 'success' : 'warning'} />
          </span>
          {confPct !== null && (
            <span
              className={
                confLow
                  ? 'asklaw-source-card__conf asklaw-source-card__conf--low'
                  : 'asklaw-source-card__conf'
              }
              title={
                confLow
                  ? lang === 'vi'
                    ? 'Điểm khớp thấp, vui lòng kiểm tra văn bản gốc'
                    : 'Low match score, please verify with the source'
                  : lang === 'vi'
                    ? 'Mức độ khớp'
                    : 'Match score'
              }
              aria-label={
                lang === 'vi' ? `Độ khớp ${confPct}%` : `Match ${confPct}%`
              }
            >
              {confLow && <IconTriangle />}
              <span>{confPct}%</span>
            </span>
          )}
        </div>
      </header>

      <div className="asklaw-source-card__meta">
        {docMeta.doc_number && <span>{lang === 'vi' ? 'Số' : 'No.'} {docMeta.doc_number}</span>}
        {docMeta.doc_number && docMeta.issuer && (
          <span className="asklaw-source-card__meta-sep" aria-hidden="true">|</span>
        )}
        {docMeta.issuer && <span>{docMeta.issuer}</span>}
      </div>

      {ref.provision_path && (
        <div className="asklaw-source-card__path">{ref.provision_path}</div>
      )}

      {snippet && (
        <blockquote className="asklaw-source-card__snippet">{snippet}</blockquote>
      )}

      <footer className="asklaw-source-card__footer">
        <EffStatusBadge status={ref.eff_status_at_render} size="sm" />

        <a
          href={primaryUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="asklaw-source-card__primary-link"
        >
          <span>{lang === 'vi' ? 'Mở vbpl.moj.gov.vn' : 'Open vbpl.moj.gov.vn'}</span>
          <IconExternal />
        </a>

        <div className="asklaw-source-card__more-wrap" ref={dropdownRef}>
          <button
            type="button"
            className="asklaw-source-card__more-btn"
            onClick={() => setDropdownOpen((v) => !v)}
            aria-expanded={dropdownOpen}
            aria-haspopup="menu"
            aria-label={
              lang === 'vi'
                ? `Tìm trên ${otherSources.length} nguồn khác`
                : `Find on ${otherSources.length} other sources`
            }
            title={lang === 'vi' ? 'Tìm trên nguồn khác' : 'Find on other sources'}
          >
            <span>
              {lang === 'vi'
                ? `${otherSources.length} nguồn khác`
                : `${otherSources.length} sources`}
            </span>
            <IconChevron />
          </button>
          {dropdownOpen && (
            <div className="asklaw-source-card__dropdown" role="menu">
              {otherSources.map((src) => {
                const docNum = docMeta.doc_number ?? docName;
                const url = src.buildUrl(docNum, docName);
                return (
                  <a
                    key={src.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="menuitem"
                    className="asklaw-source-card__dropdown-item"
                  >
                    <span className="asklaw-source-card__dropdown-name">{src.name}</span>
                    <span className="asklaw-source-card__dropdown-desc">
                      {src.descriptor}
                    </span>
                  </a>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          className={
            saved
              ? 'asklaw-source-card__bookmark asklaw-source-card__bookmark--active'
              : 'asklaw-source-card__bookmark'
          }
          onClick={handleToggleLibrary}
          aria-label={
            saved
              ? lang === 'vi'
                ? 'Bỏ lưu khỏi thư viện'
                : 'Remove from library'
              : lang === 'vi'
                ? 'Lưu vào thư viện'
                : 'Save to library'
          }
          aria-pressed={saved}
          title={
            saved
              ? lang === 'vi'
                ? 'Đã lưu'
                : 'Saved'
              : lang === 'vi'
                ? 'Lưu vào thư viện'
                : 'Save to library'
          }
        >
          <IconBookmark filled={saved} />
        </button>
      </footer>
    </article>
  );
}

// ---- SourcePanel (replaces CitationAccordion in chat.tsx) ----

export type SourcePanelProps = {
  citations: CitationShape[];
  assistantContent?: string;
  lang: 'vi' | 'en';
  activeCidPulse: string | null;
  onLibraryChange?: () => void;
};

export function SourcePanel({
  citations,
  assistantContent,
  lang,
  activeCidPulse,
  onLibraryChange,
}: SourcePanelProps): ReactElement {
  const detailsRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    if (!activeCidPulse) return;
    if (detailsRef.current && !detailsRef.current.open) detailsRef.current.open = true;
    const card = document.querySelector(`[data-cite-card-cid="${activeCidPulse}"]`);
    if (card instanceof HTMLElement) {
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeCidPulse]);

  if (!citations || citations.length === 0) return <></>;

  const count = citations.length;

  return (
    <details className="asklaw-source-panel" ref={detailsRef} open>
      <summary className="asklaw-source-panel__summary">
        <span className="asklaw-source-panel__summary-label">
          {lang === 'vi' ? `Nguồn (${count})` : `Sources (${count})`}
        </span>
        <span className="asklaw-source-panel__summary-chev" aria-hidden="true">
          <IconChevron />
        </span>
      </summary>
      <div className="asklaw-source-panel__list">
        {citations.map((c) => (
          <SourceCard
            key={c.cid}
            citation={c}
            assistantContent={assistantContent}
            lang={lang}
            isPulsing={activeCidPulse === c.cid}
            onLibraryChange={onLibraryChange}
          />
        ))}
      </div>
      <div className="asklaw-source-panel__disclaimer" role="note">
        {lang === 'vi'
          ? 'Nội dung do AI tổng hợp tham khảo từ vbpl.moj.gov.vn - vui lòng xem văn bản gốc để xác thực. Asklaw không tạo lập quan hệ luật sư - khách hàng.'
          : 'Content summarized by AI from vbpl.moj.gov.vn for reference - please consult the source document to verify. Asklaw does not create an attorney-client relationship.'}
      </div>
    </details>
  );
}
